import React, { useRef, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { isModifiedClick } from "@/lib/link-utils";
import { Button } from "@/components/ui/button";
import { ShoppingBag, ShoppingCart, ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCart } from "../hooks/use-cart";
import { CartItemCard } from "./CartItemCard";
import { CartSummary } from "./CartSummary";
import { Checkbox } from "@/components/ui/checkbox";
import { fashionPaths } from "@/modules";
import { useTranslation } from "react-i18next";
import { CartItem, useCartStore } from "../store/cart-store";

export interface CartDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  /** Navigasyonu `<Link>` yapar; bu callback yalnızca opsiyonel yan etki içindir. */
  onViewCart?: () => void;
  className?: string;
}

export const CartDropdown: React.FC<CartDropdownProps> = ({
  isOpen,
  onClose,
  onViewCart,
  className,
}) => {
  const { t } = useTranslation();
  const { items, isEmpty, totalItems, vendorGroups, formatPrice, loadCart } =
    useCart();
  const { updateSelection } = useCartStore();
  const desktopDropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  // Loading state while a selection change is being persisted + refetched
  const [isUpdatingSelection, setIsUpdatingSelection] = useState(false);

  const handleSelectionChange = async (
    cartItemIds: string[],
    isSelected: boolean,
  ) => {
    setIsUpdatingSelection(true);
    try {
      await updateSelection(cartItemIds, isSelected);
    } finally {
      setIsUpdatingSelection(false);
    }
  };

  // Group items by vendor for display
  const groupedItems = useMemo(() => {
    if (!vendorGroups || vendorGroups.length <= 1) {
      return null; // Single vendor or no grouping needed
    }

    return vendorGroups.map((group) => ({
      ...group,
      // Map vendor group items to CartItem format using items array
      cartItems: items.filter((item) => item.vendor?.id === group.vendorId),
    }));
  }, [vendorGroups, items]);

  // Navigasyonu `<Link>` yapar; bu callback'ler yalnızca yan etkiyi (dropdown kapatma)
  // çalıştırır. CartSummary guard'ı kendi içinde uyguladığı için ona event'siz
  // versiyonları geçiyoruz; bu dosyadaki Link'ler ise guard'lı sarmalayıcıyı kullanır.
  const viewCartSideEffect = () => {
    onViewCart?.();
    onClose();
  };

  const checkoutSideEffect = () => {
    onClose();
  };

  const handleViewCart = (e: React.MouseEvent) => {
    if (isModifiedClick(e)) return;
    viewCartSideEffect();
  };

  // Dropdown içi tıklama - event'in dışa yayılmasını önle
  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Dışarı tıklandığında dropdownı kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Mobil veya masaüstü dropdownlardan herhangi birinin içinde mi kontrol et
      const isClickInside =
        (desktopDropdownRef.current &&
          desktopDropdownRef.current.contains(event.target as Node)) ||
        (mobileDropdownRef.current &&
          mobileDropdownRef.current.contains(event.target as Node));

      if (!isClickInside && isOpen) {
        onClose();
      }
    };

    // Global click listener ekle
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Mini cart her açıldığında backend'den sepeti yeniden çek
  // Böylece otomatik satıcı/stok değişiklikleri (changes) kaçırılmaz.
  useEffect(() => {
    if (!isOpen) return;
    loadCart().catch(() => {});
  }, [isOpen, loadCart]);

  // Tek bir ürün satırı: seçim checkbox'ı + ürün kartı
  const renderItemRow = (item: CartItem, key: string) => (
    <div key={key} className="flex items-start gap-2">
      <Checkbox
        aria-label={item.title}
        checked={!!item.isSelected}
        disabled={isUpdatingSelection}
        onCheckedChange={(checked) =>
          handleSelectionChange([item._id], checked === true)
        }
        className="mt-1"
      />
      <CartItemCard
        item={item}
        compact={true}
        onClose={onClose}
        className="flex-1"
      />
    </div>
  );

  // Ürün listesi (desktop ve mobile ortak) - seçim overlay'i ile birlikte
  const renderItemsList = (keyPrefix: string) => (
    <div className="relative flex-1 overflow-y-auto max-h-80">
      {/* Loading overlay while selection is persisted + refetched */}
      {isUpdatingSelection && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 backdrop-blur-[1px]">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500 dark:text-gray-400" />
        </div>
      )}
      <div className="p-4 space-y-4">
        <AnimatePresence>
          {groupedItems && groupedItems.length > 1
            ? groupedItems.flatMap((group) => {
                const merchantItemIds = group.cartItems.map(
                  (item: CartItem) => item._id,
                );
                const allSelected =
                  group.cartItems.length > 0 &&
                  group.cartItems.every((item: CartItem) => item.isSelected);

                return [
                  <div
                    key={`${keyPrefix}vh-${group.vendorId}`}
                    className="flex items-center justify-between text-xs text-muted-foreground px-1"
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        aria-label={group.vendorName}
                        checked={allSelected}
                        disabled={isUpdatingSelection}
                        onCheckedChange={(checked) =>
                          handleSelectionChange(merchantItemIds, checked === true)
                        }
                      />
                      {group.vendorLogo ? (
                        <img
                          src={group.vendorLogo}
                          alt={group.vendorName}
                          className="w-4 h-4 object-contain"
                        />
                      ) : null}
                      <span className="font-medium text-foreground">
                        {group.vendorName}
                      </span>
                      <span>({group.cartItems.length})</span>
                    </div>
                    <span className="font-medium">
                      {formatPrice(group.subtotal ?? 0)}
                    </span>
                  </div>,
                  ...group.cartItems.map((item: CartItem) =>
                    renderItemRow(
                      item,
                      `${keyPrefix}item-${group.vendorId}-${item._id}`,
                    ),
                  ),
                ];
              })
            : items.map((item) => renderItemRow(item, `${keyPrefix}item-${item._id}`))}
        </AnimatePresence>
      </div>
    </div>
  );

  // isOpen değilse hiçbir şey render etme
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - just for mobile to prevent touch on background elements */}
          <div
            className="fixed inset-0 z-[1000] bg-transparent md:hidden"
            onClick={onClose}
          />

          {/* Desktop Dropdown */}
          <motion.div
            ref={desktopDropdownRef}
            onClick={handleDropdownClick}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={cn(
              // Desktop: orijinal absolute positioning
              "absolute top-full right-0 mt-2 w-96 hidden md:block",
              "bg-bg-primary border border-border rounded-xl shadow-2xl shadow-black/10",
              "z-[1002] overflow-hidden",
              className
            )}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-tertiary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-text-primary" />
                  <h3 className="font-semibold">{t("cart.title")}</h3>
                  <span className="text-sm text-muted-foreground">
                    ({totalItems}{" "}
                    {totalItems === 1 ? t("cart.product") : t("cart.products")})
                  </span>
                </div>

                {!isEmpty && (
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="text-text-text-primary hover:text-text-primary h-auto p-1"
                  >
                    <Link to="/cart" onClick={handleViewCart}>
                      <span className="text-sm">{t("cart.viewAll")}</span>
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {/* Content - Flexbox layout for dynamic height */}
            <div className="flex flex-col">
              {isEmpty ? (
                // Empty state
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h4 className="font-medium mb-2">{t("cart.emptyCart")}</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t("cart.emptyCartDescription")}
                  </p>
                  <Button asChild variant="darkPrimary">
                    <Link to={fashionPaths.search()}>
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      {t("cart.startShopping")}
                    </Link>
                  </Button>
                </div>
              ) : (
                <>
                  {/* Cart Items - Grouped by Vendor if multiple vendors exist */}
                  {renderItemsList("")}

                  {/* Fixed Summary Section - Scroll'a dahil değil */}
                  <div className="border-t border-tertiary ">
                    <div className="p-4 space-y-3">
                      {/* Cart Summary */}
                      <CartSummary
                        compact={true}
                        onCheckout={checkoutSideEffect}
                        showCheckoutButton={true}
                        onViewCart={viewCartSideEffect}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* Mobile Dropdown */}
          <motion.div
            ref={mobileDropdownRef}
            onClick={handleDropdownClick}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={cn(
              // Mobile: fixed positioning, tam ekran genişliği
              "fixed top-16 left-1 right-1 block md:hidden",
              "bg-background border border-border rounded-xl shadow-2xl shadow-black/10",
              "z-[1002] overflow-hidden",
              className
            )}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-tertiary bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-text-primary" />
                  <h3 className="font-semibold">{t("cart.title")}</h3>
                  <span className="text-sm text-muted-foreground">
                    ({totalItems}{" "}
                    {totalItems === 1 ? t("cart.product") : t("cart.products")})
                  </span>
                </div>

                {!isEmpty && (
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="text-text-primary hover:text-text-primary/ h-auto p-1"
                  >
                    <Link to="/cart" onClick={handleViewCart}>
                      <span className="text-xs">{t("cart.viewAll")}</span>
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {/* Content - Flexbox layout for dynamic height */}
            <div className="flex flex-col">
              {isEmpty ? (
                // Empty state
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                    <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h4 className="font-medium mb-2">{t("cart.emptyCart")}</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t("cart.emptyCartDescription")}
                  </p>
                  <Button
                    asChild
                    size="sm"
                    className="bg-gradient-purple-blue hover:bg-gradient-cyan-blue"
                  >
                    <Link to={fashionPaths.search()} onClick={handleViewCart}>
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      {t("cart.startShopping")}
                    </Link>
                  </Button>
                </div>
              ) : (
                <>
                  {/* Cart Items - Grouped by Vendor if multiple vendors exist */}
                  {renderItemsList("m-")}

                  {/* Fixed Summary Section - Scroll'a dahil değil */}
                  <div className="border-t border-tertiary bg-background">
                    <div className="p-4 space-y-3">
                      {/* Cart Summary */}
                      <CartSummary
                        compact={true}
                        onCheckout={checkoutSideEffect}
                        showCheckoutButton={true}
                        onViewCart={viewCartSideEffect}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
